// types/ugc/momentTypes.ts

// ------------------------------------------------------------
// Moment Media Kind
// ------------------------------------------------------------
export type MomentMediaType = "image" | "video";

export type MomentMediaKind = "image" | "video" | null;


// ------------------------------------------------------------
// Visibility (shared with backend constants)
// ------------------------------------------------------------
export type VisibilityType =
  | "global"
  | "friends"
  | "covenant"
  | "private";

// Backward compatibility (old payloads)
export function normalizeVisibility(
  v?: string | null
): VisibilityType {
  if (v === "default" || !v) return "global";
  return v as VisibilityType;
}


// ------------------------------------------------------------
// Owner Union (minimal – UI safe)
// ------------------------------------------------------------
export type MomentOwnerType =
  | {
      type: "member";
      id: number;
      name?: string | null;
      username?: string;
      avatar_url?: string | null;
      profile_url?: string;
      is_me?: boolean;
    }
  | {
      type: "guest";
      id: number;
      name?: string | null;
      avatar_url?: string | null;
      is_me?: boolean;
    }
  | {
      type: "organization";
      id: number;
      name: string;
      logo_url?: string | null;
      profile_url?: string;
      is_me?: boolean;
    };


// ------------------------------------------------------------
// Interaction Targets (returned by backend mixins)
// ------------------------------------------------------------
export interface InteractionTarget {
  content_type: number;
  object_id: number;
}


// ------------------------------------------------------------
// Multi-photo Moment metadata
// ------------------------------------------------------------
export interface MomentImageItem {
  id: string;
  key?: string | null;
  order?: number | null;
  file_name?: string | null;
  mime_type?: string | null;
  size?: number | null;
  is_cover?: boolean | null;
  field_name?: string | null;

  // Web display helpers returned by backend.
  cdn_url?: string | null;
  image_url?: string | null;
  url?: string | null;
  signed_url?: string | null;
}

export interface MomentCoverImage {
  id?: string | null;
  field_name?: string | null;
  source?: "image_items" | "image" | string | null;
  cdn_url?: string | null;
  image_url?: string | null;
}

// ------------------------------------------------------------
// Moment DTO (full serializer payload)
// ------------------------------------------------------------
export interface MomentDTO {
  // core
  id: number;
  slug: string;

  // content
  caption?: string | null;

  // legacy media fields
  image?: string | null;
  video?: string | null;
  thumbnail?: string | null;

  // media metadata
  media_kind?: MomentMediaKind;
  image_items?: MomentImageItem[];
  cover_image_id?: string | null;
  cover_image?: MomentCoverImage | null;
  max_images?: number | null;
  audio_payload?: Record<string, unknown> | null;

  // media pipeline (preferred playback / legacy signed fields)
  image_key?: string | null;
  image_signed_url?: string | null;

  video_key?: string | null;
  video_signed_url?: string | null;

  thumbnail_key?: string | null;
  thumbnail_signed_url?: string | null;

  // visibility / ui
  visibility: VisibilityType;
  is_hidden: boolean;

  // interaction counters
  comments_count: number;
  recomments_count: number;
  reactions_count: number;
  reactions_breakdown: Record<string, number>;

  // timestamps
  published_at: string;
  updated_at?: string | null;

  // media pipeline
  is_converted: boolean;

  // interaction hooks
  comment_target: InteractionTarget;
  reaction_target: InteractionTarget;

  // ownership
  owner: MomentOwnerType;
}


// ------------------------------------------------------------
// Moment Brief (for feed / lists)
// ------------------------------------------------------------
export interface MomentBrief {
  id: number;
  slug: string;

  caption?: string | null;

  media_type: MomentMediaType;
  media_kind?: MomentMediaKind;

  image_signed_url?: string | null;
  video_signed_url?: string | null;
  thumbnail_signed_url?: string | null;

  image_items?: MomentImageItem[];
  cover_image_id?: string | null;
  cover_image?: MomentCoverImage | null;

  visibility: VisibilityType;

  reactions_count: number;
  comments_count: number;

  published_at: string;
  is_converted: boolean;

  owner: MomentOwnerType;
}


// ------------------------------------------------------------
// Create Moment Payloads
// ------------------------------------------------------------
export interface CreateMomentImagePayload {
  caption?: string | null;

  /**
   * New multi-photo field.
   * The API sends these as repeated multipart field: images.
   */
  images: File[];

  /**
   * Optional UX-only index.
   * Since backend uses the first uploaded image as initial cover,
   * API helper will move this image to the first position before upload.
   */
  coverImageIndex?: number;

  visibility?: VisibilityType;
}

export interface CreateMomentLegacyImagePayload {
  caption?: string | null;

  /**
   * Backward compatibility for old web callers.
   */
  image: File;

  thumbnail?: File | null;
  visibility?: VisibilityType;
}

export interface CreateMomentVideoPayload {
  caption?: string | null;
  video: File;
  thumbnail?: File | null;
  visibility?: VisibilityType;
}

export type CreateMomentPayload =
  | CreateMomentImagePayload
  | CreateMomentLegacyImagePayload
  | CreateMomentVideoPayload;


// ------------------------------------------------------------
// Update Moment Payloads
// ------------------------------------------------------------
export interface UpdateMomentPayload {
  caption?: string | null;
  visibility?: VisibilityType;

  /**
   * Photo Moments only.
   * Must be one of image_items[].id.
   */
  cover_image_id?: string | null;

  /**
   * Video Moments only.
   * Replaces video thumbnail.
   */
  thumbnail?: File | null;
}


// ------------------------------------------------------------
// Moment Feed Response
// Cursor-based or page-based response
// ------------------------------------------------------------
export interface MomentFeedResponse {
  results: MomentDTO[];
  next?: string | null;
  previous?: string | null;
  count?: number;
}


// ------------------------------------------------------------
// Helpers (UI-level guards)
// ------------------------------------------------------------
export function getMomentMediaType(moment: MomentDTO): MomentMediaType {
  if (moment.media_kind === "video") return "video";
  if (moment.media_kind === "image") return "image";
  return moment.video ? "video" : "image";
}

export function isVideoMoment(moment: MomentDTO): boolean {
  return getMomentMediaType(moment) === "video";
}

export function isPhotoMoment(moment: MomentDTO): boolean {
  return getMomentMediaType(moment) === "image";
}

export function getMomentImageItems(moment: MomentDTO): MomentImageItem[] {
  return Array.isArray(moment.image_items)
    ? [...moment.image_items].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      )
    : [];
}

export function isMultiPhotoMoment(moment: MomentDTO): boolean {
  return isPhotoMoment(moment) && getMomentImageItems(moment).length > 1;
}

export function getMomentCoverImageItem(
  moment: MomentDTO
): MomentImageItem | undefined {
  const items = getMomentImageItems(moment);
  if (!items.length) return undefined;

  const coverID = moment.cover_image_id || moment.cover_image?.id || undefined;

  if (coverID) {
    const pinned = items.find((item) => item.id === coverID);
    if (pinned) return pinned;
  }

  const marked = items.find((item) => item.is_cover);
  if (marked) return marked;

  return items[0];
}

export function getMomentCoverFieldName(moment: MomentDTO): string {
  if (isVideoMoment(moment)) return "thumbnail";

  if (moment.cover_image?.field_name) {
    return moment.cover_image.field_name;
  }

  const cover = getMomentCoverImageItem(moment);
  if (cover?.field_name) return cover.field_name;
  if (cover?.id) return `image_items:${cover.id}`;

  if (moment.cover_image_id) {
    return `image_items:${moment.cover_image_id}`;
  }

  return "image";
}

export function getMomentImageFieldNames(moment: MomentDTO): string[] {
  if (isVideoMoment(moment)) return ["thumbnail"];

  const items = getMomentImageItems(moment);
  if (!items.length) return [getMomentCoverFieldName(moment)];

  const cover = getMomentCoverImageItem(moment);
  const coverID = cover?.id;

  const ordered = coverID
    ? [
        ...items.filter((item) => item.id === coverID),
        ...items.filter((item) => item.id !== coverID),
      ]
    : items;

  return ordered.map((item) => item.field_name || `image_items:${item.id}`);
}